import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AttachFile, Send } from '@mui/icons-material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Input,
  LinearProgress,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import { useWSContext } from '../../contexts/WebSocketContext';
import serverFile from '../../services/serverFile';
import {
  androidCancelNativeAudioRecording,
  androidHasMicrophonePermission,
  androidIsNativeAudioRecording,
  androidRequestMicrophonePermission,
  androidStartNativeAudioRecording,
  androidStopNativeAudioRecording,
  isAudioDisabledDevice,
} from '../../controller/android-interface-controller';

function AudioReplyInfo({ url }) {
  const [durationText, setDurationText] = useState('--:--');

  useEffect(() => {
    if (!url) {
      setDurationText('--:--');
      return undefined;
    }
    const audio = new Audio(url);
    audio.preload = 'metadata';

    const setFromSeconds = (secondsValue) => {
      const seconds = Math.max(0, Math.floor(secondsValue || 0));
      const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
      const ss = String(seconds % 60).padStart(2, '0');
      setDurationText(`${mm}:${ss}`);
    };

    const trySetDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setFromSeconds(audio.duration);
        return;
      }

      const originalTime = audio.currentTime || 0;
      const restore = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setFromSeconds(audio.duration);
        }
        audio.currentTime = originalTime;
        audio.removeEventListener('timeupdate', restore);
      };
      audio.currentTime = 1e101;
      audio.addEventListener('timeupdate', restore);
    };

    audio.addEventListener('loadedmetadata', trySetDuration);
    audio.addEventListener('durationchange', trySetDuration);
    audio.load();
    return () => {
      audio.removeEventListener('loadedmetadata', trySetDuration);
      audio.removeEventListener('durationchange', trySetDuration);
      audio.src = '';
    };
  }, [url]);

  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
      {durationText}
    </Typography>
  );
}

function TypingIndicator({ label, color = 'text.primary' }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.9,
        whiteSpace: 'nowrap',
        ml: 2,
      }}
    >
      <Typography variant="caption" sx={{ color }}>
        {label}
      </Typography>
      <Box sx={{ display: 'inline-flex', gap: 0.4, alignItems: 'center' }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: color,
              opacity: 0.35,
              animation: 'typingDots 1.1s infinite ease-in-out',
              animationDelay: `${i * 0.18}s`,
              '@keyframes typingDots': {
                '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.3 },
                '40%': { transform: 'translateY(-3px)', opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// Prevent ResizeObserver loop errors
/* eslint-disable class-methods-use-this */
if (typeof window !== 'undefined' && !window.ResizeObserver) {
  window.ResizeObserver = class {
    observe() {}

    unobserve() {}

    disconnect() {}
  };
}
/* eslint-enable class-methods-use-this */

const PREFIX = 'ChatFooter';
const classes = {
  alert: `${PREFIX}-alert`,
  progressWrapper: `${PREFIX}-progressWrapper`,
  inputWrapper: `${PREFIX}-inputWrapper`,
  sendButton: `${PREFIX}-sendButton`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.alert}`]: {
    width: '50%',
    margin: '0 auto',
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.progressWrapper}`]: {
    position: 'relative',
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.inputWrapper}`]: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    padding: theme.spacing(2),
    boxShadow: theme.shadows[10],
    backgroundColor: theme.palette.background.paper,
    '& textarea': { resize: 'none' },
  },
  [`& .${classes.sendButton}`]: {
    borderRadius: '0 5px 5px 0',
    padding: theme.spacing(1),
  },
}));

// Named component for proper displayName
function ChatFooterComponent({
  newMessageClient = () => {},
  encerrarChat = false,
  replyTarget = null,
  onCancelReply = () => {},
}) {
  const theme = useTheme();
  const { reiniciarTimer } = useWSContext();
  const {
    supportName,
    supportIsWriting,
    supportIsRecording,
    supportLeftMessage,
    handleClientTyping,
    handleClientRecordingStatus,
    handleSendMessageText,
    handleSendMessageFile,
    status,
  } = useWSContext();

  const userSession = useRef(
    JSON.parse(localStorage.getItem('tecnozap_user_session')) ?? {
      numSerie: 'default_value',
    }
  );
  const [msgText, setMsgText] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Problema ao enviar o arquivo');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const selectedMimeTypeRef = useRef('audio/webm');
  const shouldDiscardRecordingRef = useRef(false);
  const recordingTimerRef = useRef(null);
  const recordingElapsedRef = useRef(0);
  const autoStopTriggeredRef = useRef(false);
  const recordingLimitHandlerRef = useRef(null);
  const stopAudioRecordingRef = useRef(null);
  const usingNativeRecorderRef = useRef(false);
  const [audioBlockedForDevice, setAudioBlockedForDevice] = useState(() => isAudioDisabledDevice());
  const isAndroidWebView = useMemo(
    () => typeof window !== 'undefined' && typeof window.Android === 'object',
    []
  );
  const fileAccept = audioBlockedForDevice
    ? 'image/png, image/pjg, image/jpeg, application/pdf'
    : 'image/png, image/pjg, image/jpeg,video/mp4, video/3gpp, video/mov, application/pdf,audio/webm,audio/ogg,audio/mpeg,audio/mp4,audio/wav,audio/x-m4a';

  const shouldDisableBySocketStatus = !isAndroidWebView && status !== 'connected';
  const isChatDisabled =
    Boolean(supportLeftMessage) ||
    (typeof encerrarChat === 'string' ? encerrarChat === 'true' : !!encerrarChat) ||
    shouldDisableBySocketStatus;
  const hasTypedMessage = msgText.trim().length > 0;
  const MAX_RECORDING_SECONDS = 90;


  const normalizeReplyPayload = useCallback(
    (msg) => {
      if (!msg) return null;
      return {
        id: msg.id,
        userType: msg.userType,
        msgType: msg.msgType,
        msgText: msg.msgText || '',
        urlFile: msg.urlFile || null,
        msgFileType:
          msg.msgFileType ||
          (msg.urlFile ? `${msg.urlFile}`.split('.').pop()?.toLowerCase() : null),
      };
    },
    []
  );

  const getMediaReplyLabel = useCallback((msg) => {
    const rawType =
      (msg?.msgFileType || `${msg?.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(rawType)) return 'Foto';
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(rawType)) return 'Audio';
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(rawType)) return 'Video';
    if (rawType === 'pdf') return 'Documento PDF';
    return 'Arquivo';
  }, []);

  const getReplyPreview = useCallback((msg) => {
    if (!msg) return '';
    if (msg.msgType === 'file') return getMediaReplyLabel(msg);
    return msg.msgText || 'Mensagem';
  }, [getMediaReplyLabel]);

  const stopTypingIndicator = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    handleClientTyping(false);
  }, [handleClientTyping]);

  const renderReplyPreview = useCallback((msg) => {
    if (!msg) return null;
    if (msg.msgType !== 'file') {
      return <Typography variant="body2">{msg.msgText || 'Mensagem'}</Typography>;
    }

    const fileType =
      (msg?.msgFileType || `${msg?.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    const url = msg?.urlFile || '';
    const label = getReplyPreview(msg);
    const commonThumbSx = {
      width: 44,
      height: 44,
      borderRadius: 1,
      objectFit: 'cover',
      border: '1px solid rgba(0,0,0,0.12)',
      bgcolor: 'background.paper',
    };

    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(fileType) && url) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src={url} alt="midia" sx={commonThumbSx} />
          <Typography variant="body2">{label}</Typography>
        </Box>
      );
    }
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(fileType) && url) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="video" src={url} muted sx={commonThumbSx} />
          <Typography variant="body2">{label}</Typography>
        </Box>
      );
    }
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GraphicEqIcon fontSize="small" />
          <Box>
            <Typography variant="body2">{label}</Typography>
            <AudioReplyInfo url={url} />
          </Box>
        </Box>
      );
    }
    if (fileType === 'pdf') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon fontSize="small" />
          <Typography variant="body2">{label}</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InsertDriveFileIcon fontSize="small" />
        <Typography variant="body2">{label}</Typography>
      </Box>
    );
  }, [getReplyPreview]);

  const formatRecordingTime = useCallback((totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  const sendMessage = useCallback(() => {
    const text = msgText.trim();
    if (!text) return;
    stopTypingIndicator();
    const replyTo = normalizeReplyPayload(replyTarget);
    const message = {
      id: Date.now(),
      userType: 'cliente',
      msgType: 'txt',
      msgText: text,
      urlFile: null,
      replyTo,
      date: format(new Date(), 'HH:mm:ss'),
    };
    handleSendMessageText(text, replyTo);
    newMessageClient(message);
    reiniciarTimer?.();
    setMsgText('');
    onCancelReply();
  }, [msgText, stopTypingIndicator, handleSendMessageText, newMessageClient, reiniciarTimer, replyTarget, normalizeReplyPayload, onCancelReply]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const userTypingHandler = useCallback(
    (value) => {
      setMsgText(value);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (value.trim()) {
        handleClientTyping(true);
        typingTimeoutRef.current = setTimeout(() => {
          handleClientTyping(false);
          typingTimeoutRef.current = null;
        }, 1000);
      } else {
        handleClientTyping(false);
      }
    },
    [handleClientTyping]
  );

  const uploadFile = async (file) => {
    if (!file) return;
    stopTypingIndicator();
    setUploadProgress(0);
    if (
      audioBlockedForDevice &&
      (file.type?.startsWith('audio/') || file.type?.startsWith('video/'))
    ) {
      setErrorMessage('Neste aparelho, audio e video estao desabilitados para esta conversa.');
      setError(true);
      setTimeout(() => setError(false), 8000);
      return;
    }
    const replyTo = normalizeReplyPayload(replyTarget);
    setError(false);
    try {
      const data = new FormData();
      data.append('file', file);
      const response = await serverFile.post(
        `/save/${userSession.current.numSerie}`,
        data,
        {
          onUploadProgress: (e) => {
            const total = Number(e?.total) || 0;
            if (total <= 0) return;
            const progress = Math.min(100, Math.max(0, Math.round((e.loaded * 100) / total)));
            setUploadProgress(progress);
          },
        }
      );
      const fileName =
        typeof response.data === 'string' ? response.data : response.data.fileName;
      const fileUrl = `${process.env.REACT_APP_URL_ARQUIVOS}/${userSession.current.numSerie}/${fileName}`;
      const message = {
        id: Date.now(),
        userType: 'cliente',
        msgType: 'file',
        msgText: '',
        urlFile: fileUrl,
        replyTo,
        date: format(new Date(), 'HH:mm:ss'),
      };
      handleSendMessageFile(fileUrl, replyTo);
      newMessageClient(message);
      reiniciarTimer?.();
      onCancelReply();
      setTimeout(() => setUploadProgress(0), 1000);
    } catch (err) {
      console.error('Erro ao enviar arquivo', {
        message: err?.message,
        status: err?.response?.status,
        data: err?.response?.data,
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size,
      });
      setErrorMessage('Problema ao enviar o arquivo');
      setError(true);
      setUploadProgress(0);
      setTimeout(() => setError(false), 8000);
    }
  };

  const openNativeAudioCaptureFallback = () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'audio/*';
      input.setAttribute('capture', 'microphone');
      input.onchange = () => {
        const selected = input.files?.[0];
        if (selected) {
          uploadFile(selected);
        }
      };
      input.click();
    } catch (fallbackErr) {
      console.error('Erro ao abrir gravador nativo de fallback', fallbackErr);
    }
  };

  const startRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = setInterval(() => {
      recordingElapsedRef.current += 1;
      const next = recordingElapsedRef.current;
      setRecordingSeconds(next);

      if (next >= MAX_RECORDING_SECONDS && !autoStopTriggeredRef.current) {
        autoStopTriggeredRef.current = true;
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
        if (typeof recordingLimitHandlerRef.current === 'function') {
          recordingLimitHandlerRef.current();
        }
      }
    }, 1000);
  }, [MAX_RECORDING_SECONDS]);

  const startRecordingUi = useCallback((onLimitReached = null) => {
    recordingLimitHandlerRef.current = onLimitReached;
    recordingElapsedRef.current = 0;
    autoStopTriggeredRef.current = false;
    setRecordingSeconds(0);
    startRecordingTimer();
    handleClientTyping(false);
    handleClientRecordingStatus(true);
    setIsRecording(true);
  }, [handleClientRecordingStatus, handleClientTyping, startRecordingTimer]);

  const stopRecordingUi = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    recordingElapsedRef.current = 0;
    autoStopTriggeredRef.current = false;
    recordingLimitHandlerRef.current = null;
    setRecordingSeconds(0);
    handleClientRecordingStatus(false);
    setIsRecording(false);
  }, [handleClientRecordingStatus]);

  const base64ToFile = useCallback((base64, fileName = `audio_${Date.now()}.m4a`, mimeType = 'audio/mp4') => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: mimeType });
  }, []);

  const startNativeAndroidRecording = useCallback(() => {
    const hasNativePermission = androidHasMicrophonePermission();
    if (!hasNativePermission) {
      androidRequestMicrophonePermission();
      setErrorMessage('Permissão de microfone solicitada. Toque novamente para gravar.');
      setError(true);
      setTimeout(() => setError(false), 8000);
      return false;
    }

    const started = androidStartNativeAudioRecording();
    if (!started) {
      setErrorMessage('Não foi possível iniciar o microfone nativo no Android.');
      setError(true);
      setTimeout(() => setError(false), 8000);
      return false;
    }

    usingNativeRecorderRef.current = true;
    startRecordingUi(() => {
      stopAudioRecordingRef.current?.();
      setErrorMessage('Tempo máximo de 1:30 atingido. Áudio finalizado.');
      setError(true);
      setTimeout(() => setError(false), 5000);
    });
    return true;
  }, [startRecordingUi]);

  const startAudioRecording = async () => {
    if (isChatDisabled || isRecording) return;
    if (audioBlockedForDevice) {
      setErrorMessage('Audio desabilitado neste dispositivo.');
      setError(true);
      setTimeout(() => setError(false), 8000);
      return;
    }

    if (isAndroidWebView) {
      startNativeAndroidRecording();
      return;
    }

    if (window.isSecureContext === false) {
      setErrorMessage('Microfone indisponível em HTTP no celular. Use HTTPS para gravar áudio.');
      setError(true);
      setTimeout(() => setError(false), 8000);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErrorMessage('Seu navegador/app não suporta gravação de áudio nesta tela.');
      setError(true);
      setTimeout(() => setError(false), 8000);
      return;
    }

    // Evita lock do microfone por stream anterior preso no WebView.
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    let acquiredStream = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      acquiredStream = stream;
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      const preferredMimeTypes = [
        'audio/mp4',
        'audio/x-m4a',
        'audio/mpeg',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
      ];
      const selectedMimeType = preferredMimeTypes.find((type) =>
        MediaRecorder.isTypeSupported(type)
      );
      const recorder = selectedMimeType
        ? new MediaRecorder(stream, {
            mimeType: selectedMimeType,
            audioBitsPerSecond: 128000,
          })
        : new MediaRecorder(stream);
      handleClientTyping(false);
      mediaRecorderRef.current = recorder;
      selectedMimeTypeRef.current = selectedMimeType || recorder.mimeType || 'audio/webm';
      shouldDiscardRecordingRef.current = false;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        try {
          if (shouldDiscardRecordingRef.current) {
            return;
          }
          if (audioChunksRef.current.length === 0) {
            throw new Error('Gravacao de audio vazia');
          }
          const mimeType = selectedMimeTypeRef.current || recorder.mimeType || 'audio/webm';
          let extension = 'webm';
          if (mimeType.includes('ogg')) extension = 'ogg';
          if (mimeType.includes('mp4')) extension = 'm4a';
          if (mimeType.includes('mpeg')) extension = 'mp3';
          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          const fileName = `audio_${Date.now()}.${extension}`;
          const audioFile = new File([blob], fileName, { type: mimeType });
          await uploadFile(audioFile);
        } finally {
          handleClientRecordingStatus(false);
          if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach((track) => track.stop());
            audioStreamRef.current = null;
          }
          mediaRecorderRef.current = null;
          audioChunksRef.current = [];
          shouldDiscardRecordingRef.current = false;
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
          }
          recordingElapsedRef.current = 0;
          autoStopTriggeredRef.current = false;
          setRecordingSeconds(0);
        }
      };

      recorder.start(250);
      startRecordingUi(() => {
        stopAudioRecordingRef.current?.();
        setErrorMessage('Tempo máximo de 1:30 atingido. Áudio finalizado.');
        setError(true);
        setTimeout(() => setError(false), 5000);
      });
    } catch (err) {
      if (acquiredStream) {
        acquiredStream.getTracks().forEach((track) => track.stop());
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      }
      mediaRecorderRef.current = null;
      recordingElapsedRef.current = 0;
      autoStopTriggeredRef.current = false;
      handleClientRecordingStatus(false);
      setIsRecording(false);

      console.error('Erro ao iniciar gravacao de audio', err);
      if (err?.name === 'NotAllowedError') {
        androidRequestMicrophonePermission();
        setErrorMessage('Microfone bloqueado. Conceda a permissão e toque novamente.');
      } else if (err?.name === 'NotReadableError') {
        setErrorMessage('Não foi possível iniciar o microfone inline. Abrindo gravador nativo...');
        openNativeAudioCaptureFallback();
      } else {
        setErrorMessage('Não foi possível acessar o microfone. Verifique permissão e HTTPS.');
      }
      setError(true);
      setTimeout(() => setError(false), 8000);
    }
  };

  async function stopAudioRecording() {
    if (usingNativeRecorderRef.current || androidIsNativeAudioRecording()) {
      const result = androidStopNativeAudioRecording();
      usingNativeRecorderRef.current = false;
      stopRecordingUi();
      if (!result?.ok || !result?.base64) {
        setErrorMessage('Falha ao finalizar gravação nativa no Android.');
        setError(true);
        setTimeout(() => setError(false), 8000);
        return;
      }
      try {
        const audioFile = base64ToFile(
          result.base64,
          result.fileName || `audio_${Date.now()}.m4a`,
          result.mimeType || 'audio/mp4'
        );
        await uploadFile(audioFile);
      } catch (err) {
        console.error('Erro ao converter/enviar áudio nativo do Android', err);
        setErrorMessage('Falha ao enviar áudio gravado no Android.');
        setError(true);
        setTimeout(() => setError(false), 8000);
      }
      return;
    }

    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      // Evita UI "presa" em gravacao quando o recorder ja foi finalizado externamente.
      stopRecordingUi();
      return;
    }
    handleClientRecordingStatus(false);
    recorder.stop();
    recordingElapsedRef.current = 0;
    autoStopTriggeredRef.current = false;
    setIsRecording(false);
  }

  function cancelAudioRecording() {
    if (usingNativeRecorderRef.current || androidIsNativeAudioRecording()) {
      androidCancelNativeAudioRecording();
      usingNativeRecorderRef.current = false;
      stopRecordingUi();
      return;
    }

    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      // Evita UI "presa" em gravacao quando o recorder ja foi finalizado externamente.
      stopRecordingUi();
      return;
    }
    shouldDiscardRecordingRef.current = true;
    handleClientRecordingStatus(false);
    recorder.stop();
    recordingElapsedRef.current = 0;
    autoStopTriggeredRef.current = false;
    setIsRecording(false);
  }
  stopAudioRecordingRef.current = stopAudioRecording;

  useEffect(() => {
    if (!isRecording) return undefined;

    const watchdogId = setInterval(() => {
      const recorderActive =
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive';
      const nativeActive =
        usingNativeRecorderRef.current || androidIsNativeAudioRecording();

      if (!recorderActive && !nativeActive) {
        stopRecordingUi();
        return;
      }

      if (!recordingTimerRef.current) {
        startRecordingTimer();
      }
    }, 800);

    return () => clearInterval(watchdogId);
  }, [isRecording, startRecordingTimer, stopRecordingUi]);

  const getMainActionIcon = () => {
    if (isChatDisabled) return <Send />;
    if (isRecording) return <StopIcon />;
    if (audioBlockedForDevice) return <Send />;
    if (hasTypedMessage) return <Send />;
    return <MicIcon />;
  };

  useEffect(() => {
    const input = document.getElementById('campoMensagem');
    input?.addEventListener('keydown', handleKeyDown);
    return () => {
      input?.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 30;
    const syncDeviceAudioState = () => {
      const blocked = isAudioDisabledDevice();
      setAudioBlockedForDevice(blocked);
      if (blocked) return true;
      attempts += 1;
      return attempts >= maxAttempts;
    };

    if (syncDeviceAudioState()) return undefined;
    const timer = setInterval(() => {
      if (syncDeviceAudioState()) {
        clearInterval(timer);
      }
    }, 1000);

    const onFocus = () => syncDeviceAudioState();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncDeviceAudioState();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(
    () => () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      handleClientTyping(false);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        shouldDiscardRecordingRef.current = true;
        handleClientRecordingStatus(false);
        mediaRecorderRef.current.stop();
      }
      if (usingNativeRecorderRef.current || androidIsNativeAudioRecording()) {
        androidCancelNativeAudioRecording();
        usingNativeRecorderRef.current = false;
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      recordingElapsedRef.current = 0;
      autoStopTriggeredRef.current = false;
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    },
    []
  );

  useEffect(() => {
    const clearTypingOnBackground = () => stopTypingIndicator();
    const clearTypingOnVisibility = () => {
      if (document.visibilityState !== 'visible') {
        stopTypingIndicator();
      }
    };

    window.addEventListener('blur', clearTypingOnBackground);
    window.addEventListener('beforeunload', clearTypingOnBackground);
    document.addEventListener('visibilitychange', clearTypingOnVisibility);

    return () => {
      window.removeEventListener('blur', clearTypingOnBackground);
      window.removeEventListener('beforeunload', clearTypingOnBackground);
      document.removeEventListener('visibilitychange', clearTypingOnVisibility);
    };
  }, [stopTypingIndicator]);

  return (
    <StyledBox>
      {error && (
        <Alert severity="error" className={classes.alert}>
          {errorMessage}
        </Alert>
      )}
      {uploadProgress > 0 && (
        <Box className={classes.progressWrapper}>
          <Typography variant="body2" align="center">
            {uploadProgress}%
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}
      {supportIsRecording && (
        <TypingIndicator
          label={`${supportName} está gravando áudio`}
          color={theme.palette.text.primary}
        />
      )}
      {replyTarget && (
        <Box
          sx={{
            mx: 2,
            mb: 1,
            p: 1,
            borderRadius: 1,
            border: '1px solid rgba(0,0,0,0.15)',
            borderLeft: '3px solid #0288d1',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Respondendo
            </Typography>
            {renderReplyPreview(replyTarget)}
          </Box>
          <Button size="small" onClick={onCancelReply}>
            Cancelar
          </Button>
        </Box>
      )}
      {!supportIsRecording && supportIsWriting && (
        <TypingIndicator
          label={`${supportName} está digitando`}
          color={theme.palette.text.primary}
        />
      )}
      <Box className={classes.inputWrapper}>
        <input
          type="file"
          accept={fileAccept}
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => uploadFile(e.target.files[0])}
          disabled={isChatDisabled}
        />
        {!isRecording && (
          <IconButton
            onClick={() => {
              reiniciarTimer?.();
              fileInputRef.current?.click();
            }}
            color="primary"
            disabled={isChatDisabled}
            sx={
              isChatDisabled
                ? {
                    color: (t) => t.palette.action.disabled,
                    opacity: 1,
                  }
                : undefined
            }
          >
            <AttachFile />
          </IconButton>
        )}
        {isRecording && (
          // eslint-disable-next-line react/jsx-no-bind
          <IconButton onClick={cancelAudioRecording} color="error" disabled={isChatDisabled}>
            <DeleteOutlineIcon />
          </IconButton>
        )}
        <Input
          id="campoMensagem"
          value={isRecording ? `Gravando • ${formatRecordingTime(recordingSeconds)}` : msgText}
          onChange={(e) => {
            if (isRecording) return;
            userTypingHandler(e.target.value);
          }}
          placeholder={isChatDisabled ? 'Chat encerrado' : 'Digite aqui..'}
          disabled={isChatDisabled || isRecording}
          multiline
          maxRows={4}
          fullWidth
          sx={{
            ml: 2,
            flex: 1,
            bgcolor: theme.palette.grey[400],
            borderRadius: '5px 0px 3px 3px',
            p: 1,
            '& .MuiInputBase-input': {
              color: isRecording ? 'rgba(255,255,255,0.92)' : theme.palette.text.primary,
              fontSize: isRecording ? '1.15rem' : '1rem',
              fontWeight: 400,
              textAlign: isRecording ? 'center' : 'left',
              animation: isRecording ? 'recordBlink 1.4s ease-in-out infinite' : 'none',
              '@keyframes recordBlink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.42 },
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: theme.palette.text.secondary,
            },
          }}
        />
        <Button
          onClick={() => {
            if (isRecording) {
              stopAudioRecording();
              return;
            }
            if (audioBlockedForDevice) {
              if (hasTypedMessage) sendMessage();
              return;
            }
            if (hasTypedMessage) {
              sendMessage();
              return;
            }
            startAudioRecording();
          }}
          disabled={isChatDisabled}
          className={classes.sendButton}
          variant="contained"
          sx={
            isChatDisabled
              ? {
                  bgcolor: (t) => t.palette.action.disabledBackground,
                  color: (t) => t.palette.action.disabled,
                  opacity: 1,
                }
              : undefined
          }
        >
          {getMainActionIcon()}
        </Button>
      </Box>
    </StyledBox>
  );
}

const ChatFooter = React.memo(ChatFooterComponent);
ChatFooter.displayName = 'ChatFooter';

ChatFooter.propTypes = {
  newMessageClient: PropTypes.func,
  encerrarChat: PropTypes.bool,
  replyTarget: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    msgType: PropTypes.string,
    msgText: PropTypes.string,
    urlFile: PropTypes.string,
  }),
  onCancelReply: PropTypes.func,
};

export default ChatFooter;



